'use client';

import * as React from 'react';
import { Toaster as SonnerToaster } from 'sonner';
import { DialogToastRefContext } from './DialogToastContext';

import {
  createContext,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';

export enum ToastView {
  Normal = 'normal',
  Map = 'map',
}

interface ToastContextProps {
  toastView: ToastView;
  setToastView: (toastView: ToastView) => void;
}

export const ToastContext = createContext<ToastContextProps | undefined>(
  undefined,
);

// In E2E tests, toasts auto-dismiss too fast (5s) for Playwright assertions.
// Increase to 15s so tests can reliably verify success/error toasts.
const E2E_TOAST_DURATION = 15000;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isE2E =
  typeof import.meta !== 'undefined' &&
  (import.meta as any).env?.VITE_E2E_TEST === 'true';

export const SONNER_COMMON_PROPS = {
  richColors: true,
  position: 'bottom-right' as const,
  duration: isE2E ? E2E_TOAST_DURATION : undefined,
};

/**
 * Observes the `dark` class on `<html>` to determine the effective theme.
 * This decouples from any specific context provider, so it works anywhere
 * in the component tree — including above ThemeProvider.
 */
function getDocumentTheme(): 'light' | 'dark' {
  return typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')
    ? 'dark'
    : 'light';
}

function subscribeToDocumentTheme(callback: () => void): () => void {
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  return () => observer.disconnect();
}

const SERVER_THEME = 'light' as const;

export function useDocumentTheme(): 'light' | 'dark' {
  return useSyncExternalStore(
    subscribeToDocumentTheme,
    getDocumentTheme,
    () => SERVER_THEME,
  );
}

/** Complete Sonner props with theme derived from the document `dark` class. */
export function useSonnerProps() {
  const theme = useDocumentTheme();
  return {
    ...SONNER_COMMON_PROPS,
    theme,
    // Inline style overrides Sonner's injected dark-mode default (#000 → popover blue)
    ...(theme === 'dark' && {
      style: {
        '--normal-bg': 'hsl(var(--popover))',
        '--normal-border': 'hsl(var(--border))',
        '--normal-text': 'hsl(var(--popover-foreground))',
      } as React.CSSProperties,
    }),
  };
}

export function Toaster() {
  const sonnerProps = useSonnerProps();
  return <SonnerToaster {...sonnerProps} />;
}

interface ToasterInDialogProps {
  toasts?: unknown[];
  children: React.ReactNode;
}

export function ToasterInDialog({ children }: ToasterInDialogProps) {
  const sonnerProps = useSonnerProps();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const hasParentToaster = useContext(ToastContext) !== undefined;

  return (
    <DialogToastRefContext.Provider value={containerRef}>
      {!hasParentToaster && <SonnerToaster {...sonnerProps} />}
      <div ref={containerRef} className="relative">
        {children}
      </div>
    </DialogToastRefContext.Provider>
  );
}

interface ToasterWithViewStateProps {
  children: React.ReactNode;
}

export function ToasterWithViewState({ children }: ToasterWithViewStateProps) {
  const sonnerProps = useSonnerProps();
  const [toastView, setToastView] = useState<ToastView>(ToastView.Normal);

  const contextValue = useMemo(
    () => ({
      toastView,
      setToastView,
    }),
    [toastView, setToastView],
  );

  const showGlobalToaster = toastView !== ToastView.Map;

  return (
    <ToastContext.Provider value={contextValue}>
      {showGlobalToaster && <SonnerToaster {...sonnerProps} />}
      {children}
    </ToastContext.Provider>
  );
}

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error(
      'useToastContext must be used within a ToasterWithViewState',
    );
  }
  return context;
};
